import { useCallback, useState } from 'react'

/** Min width of the issue drawer (the right pane) when the 3-pane map · list · drawer layout is open. */
export const DRAWER_MIN_PX = 360
/** The drawer never grows past half the viewport (keeps the map + list usable). */
export const DRAWER_MAX_VW = 0.5

export interface DrawerResize {
  /** Drawer width in px; null = default (50% of the aside ≈ ⅓ of screen). */
  widthPx: number | null
  /** pointerdown handler for the list↔drawer drag handle. */
  onHandlePointerDown: (e: React.PointerEvent<HTMLDivElement>) => void
}

/**
 * Resize the issue detail drawer (the viewport-right pane) by dragging the list↔drawer divider. Mirrors
 * `useSplitPane`'s pointer logic: the drawer is flush to the viewport right (the aside is), so its width
 * is `innerWidth - clientX`, clamped to `[DRAWER_MIN_PX, DRAWER_MAX_VW·vw]`. Local state — resets to the
 * default (thirds) when the drawer closes and DetailDrawerRow unmounts; persists while drilling.
 */
export function useDrawerResize(): DrawerResize {
  const [widthPx, setWidthPx] = useState<number | null>(null)

  const onHandlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    const handle = e.currentTarget
    handle.setPointerCapture(e.pointerId)
    const onMove = (ev: PointerEvent) => {
      const w = Math.min(
        window.innerWidth * DRAWER_MAX_VW,
        Math.max(DRAWER_MIN_PX, window.innerWidth - ev.clientX),
      )
      setWidthPx(w)
    }
    const cleanup = () => {
      handle.removeEventListener('pointermove', onMove)
      handle.removeEventListener('pointerup', cleanup)
      handle.removeEventListener('pointercancel', cleanup)
    }
    handle.addEventListener('pointermove', onMove)
    handle.addEventListener('pointerup', cleanup)
    handle.addEventListener('pointercancel', cleanup)
  }, [])

  return { widthPx, onHandlePointerDown }
}
